/**
 * Payload-level transit encryption — the client half of the contract in
 * `services/shared/shared_layer/utils/transit_payload.py`.
 *
 * Sits on top of TLS as defence in depth, using the same shared key and CryptoJS envelope already
 * used for bank details.
 *
 * Requests: send the sensitive value under its normal key as ciphertext, and name the keys in
 * `encFields` so the server knows what to decrypt.
 *
 * Responses: declare support with `X-Accept-Encrypted: 1`. The server replies with
 * `X-Encrypted-Fields: emailId,mobileNoWa,...` and we decrypt exactly those keys, wherever they
 * appear in the payload, before any application code sees it — so filtering, sorting and
 * round-tripping all keep working on plaintext and no caller needs to know this happened.
 *
 * NOT confidentiality against a network attacker: the key ships in this bundle and anyone can read
 * it. What it does buy is that credentials and PII no longer sit in plain view in devtools, a proxy
 * log, a screen share or an APM trace that captured a body.
 */
import {
  encryptForTransit,
  decryptFromTransit,
  hasTransitKey,
  isOpenSslSaltedCiphertextBase64,
} from "./common";

export const ACCEPT_ENCRYPTED_HEADER = "X-Accept-Encrypted";
export const ENCRYPTED_FIELDS_HEADER = "x-encrypted-fields"; // axios lowercases response headers
export const ENCRYPTED_PROBE_HEADER = "x-encrypted-probe";
export const PROBE_PLAINTEXT = "ok";
export const REQUEST_ENC_FIELDS_KEY = "encFields";

/**
 * Whether to tell the server this bundle can decrypt.
 *
 * Two ways this turns itself off. No key configured at all — asking the server to encrypt PII we
 * then render as ciphertext is worse than not asking. And a key that does not MATCH the server's:
 * three repos configure this independently, so `noteProbeResult` below latches the mismatch off for
 * the session the first time a response proves it, rather than showing the user gibberish until
 * someone notices and redeploys.
 */
let keysAgree = true;

export const shouldAcceptEncrypted = () => hasTransitKey() && keysAgree;

/**
 * Check the server's probe. Returns true when the keys match.
 *
 * Called for every response that carries the header; the first failure stops us advertising support
 * and says exactly what is wrong, because "some fields render as base64" is otherwise a genuinely
 * baffling bug to be handed.
 */
export const noteProbeResult = (probeCiphertext) => {
  if (!probeCiphertext) return true; // Server did not send one; nothing to check.
  let plain;
  try {
    plain = decryptFromTransit(probeCiphertext);
  } catch (e) {
    plain = null;
  }
  if (plain === PROBE_PLAINTEXT) return true;
  if (keysAgree) {
    console.error(
      "Transit encryption disabled: this build's REACT_APP_TRANSIT_ENC_KEY does not match the " +
        "server's TRANSIT_ENC_KEY, so encrypted fields could not be read. Requests will fall back " +
        "to plaintext until the keys match.",
    );
  }
  keysAgree = false;
  return false;
};

/** Test seam — lets a test start from a clean slate after simulating a mismatch. */
export const _resetKeyAgreement = () => {
  keysAgree = true;
};

/**
 * Return `body` with `fields` encrypted and listed in `encFields`.
 *
 * Absent, empty and non-string values are left alone: encrypting "nothing" would make an absent
 * value look present to the server. Returns the body unchanged when no key is configured, so a
 * misconfigured environment degrades to today's behaviour instead of posting unusable ciphertext.
 */
export const encryptRequestFields = (body, fields = []) => {
  if (!body || typeof body !== "object" || !fields.length) return body;

  const out = { ...body };
  const encrypted = [];
  for (const field of fields) {
    const value = out[field];
    if (typeof value !== "string" || value === "") continue;
    const ciphertext = encryptForTransit(value);
    // encryptForTransit returns its input unchanged when no key is set — don't claim we encrypted.
    if (ciphertext === value) continue;
    out[field] = ciphertext;
    encrypted.push(field);
  }
  if (!encrypted.length) return body;
  return { ...out, [REQUEST_ENC_FIELDS_KEY]: encrypted };
};

/** Parse the `X-Encrypted-Fields` response header into a Set of field names. */
export const parseEncryptedFields = (headerValue) => {
  if (!headerValue || typeof headerValue !== "string") return null;
  const names = headerValue
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
  return names.length ? new Set(names) : null;
};

/**
 * Walk a decoded response and decrypt every occurrence of the named fields, in place on a copy.
 *
 * Only values that actually carry our ciphertext envelope are touched, so a same-named field that
 * happened not to be encrypted survives untouched rather than being mangled.
 */
export const decryptResponseFields = (payload, fieldNames) => {
  if (!fieldNames || !fieldNames.size || payload == null) return payload;

  const walk = (node) => {
    if (Array.isArray(node)) return node.map(walk);
    if (node && typeof node === "object") {
      const out = {};
      for (const [key, value] of Object.entries(node)) {
        if (fieldNames.has(key) && typeof value === "string" && isOpenSslSaltedCiphertextBase64(value)) {
          const plain = decryptFromTransit(value);
          // decryptFromTransit hands back the input on failure — keep whatever we can render.
          out[key] = plain === undefined || plain === null ? value : plain;
        } else {
          out[key] = walk(value);
        }
      }
      return out;
    }
    return node;
  };

  return walk(payload);
};

/**
 * Field names sent encrypted on each credential-bearing request.
 *
 * Keys are matched against the request URL, so one entry covers every caller of that endpoint —
 * a service that forgets to encrypt cannot leak, because the interceptor does it centrally.
 */
export const REQUEST_SENSITIVE_FIELDS = [
  { match: "/login/check-password", fields: ["password"] },
  { match: "/login/set-password", fields: ["password"] },
  { match: "/login/reset-password", fields: ["password", "otp"] },
  { match: "/login/new-account", fields: ["password"] },
  { match: "/login/delete-account", fields: ["password"] },
  { match: "/interior-designers/signup", fields: ["password"] },
  { match: "/architects/signup", fields: ["password"] },
  { match: "/freelancers/signup", fields: ["password"] },
  { match: "/admin/owners", fields: ["password"] },
  { match: "/customers/ssl", fields: ["sslPassword"] },
  // Creating a login for staff/BV users.
  { match: "/login", fields: ["password"] },
];

/** The sensitive fields to encrypt for a given request URL, if any. */
export const sensitiveFieldsForUrl = (url = "") => {
  const path = String(url).split("?")[0];
  // Longest match wins: '/login/reset-password' must beat the bare '/login' entry.
  let best = null;
  for (const entry of REQUEST_SENSITIVE_FIELDS) {
    if (path.includes(entry.match) && (!best || entry.match.length > best.match.length)) {
      best = entry;
    }
  }
  return best ? best.fields : null;
};
